/**
 * Pet Selector Modal
 * 
 * Beautiful card grid modal that appears on login when user has multiple pets.
 * Prompts: "Who are you caring for today?"
 * Uses sessionStorage to remember selection for current browser session.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Grid,
  IconButton,
  Stack,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
  alpha,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Pets as PetsIcon,
  Mail as MailIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cake as CakeIcon,
  FavoriteBorder as HeartIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Pet } from '../../types/pet';
import { useNavigate } from 'react-router-dom';
import LazyAvatar from '../LazyAvatar';

interface PetSelectorModalProps {
  open: boolean;
  pets: Pet[];
  onSelectPet: (pet: Pet) => void;
  onClose?: () => void;
  userName?: string;
  showCloseButton?: boolean;
}

const PetSelectorModal: React.FC<PetSelectorModalProps> = ({
  open,
  pets,
  onSelectPet,
  onClose,
  userName = 'there',
  showCloseButton = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoveredPetId, setHoveredPetId] = useState<string | null>(null);
  const [petStats, setPetStats] = useState<Record<string, { unreadMessages: number; alerts: number; allGood: boolean }>>({});

  // Load real stats for each pet from Firestore
  useEffect(() => {
    if (!open || pets.length === 0) return;

    pets.forEach(async (pet) => {
      try {
        // Dynamic imports to avoid top-level Firebase initialization in tests
        const { vaccinationService } = await import('../../services/vaccinationService');

        const vaccinations = await vaccinationService.getVaccinationRecords(pet.id).catch(() => [] as any[]);

        const now = new Date();
        const overdueVaccinations = vaccinations.filter((v: any) => {
          const nextDue = v.nextDueDate ? new Date(v.nextDueDate) : null;
          return nextDue && nextDue < now;
        });

        const alerts = overdueVaccinations.length;
        setPetStats(prev => ({
          ...prev,
          [pet.id]: {
            unreadMessages: 0,
            alerts,
            allGood: alerts === 0,
          },
        }));
      } catch {
        // Keep default stats on error
      }
    });
  }, [open, pets]);

  const handlePetSelect = (pet: Pet) => {
    // Store selection in sessionStorage (cleared when browser closes)
    sessionStorage.setItem('selectedPetId', pet.id);
    sessionStorage.setItem('selectedPetName', pet.name);
    
    // Call parent handler
    onSelectPet(pet);
  };

  const handleManageAllPets = () => {
    navigate('/pets/add');
    if (onClose) onClose();
  };

  // Get pet age from birthDate
  const getPetAge = (pet: Pet): string => {
    if (!pet.birthDate) return 'Age unknown';
    
    const birth = typeof pet.birthDate === 'string' 
      ? new Date(pet.birthDate) 
      : pet.birthDate;
    
    const ageYears = Math.floor((Date.now() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    const ageMonths = Math.floor((Date.now() - birth.getTime()) / (30.44 * 24 * 60 * 60 * 1000));
    
    if (ageYears >= 1) {
      return `${ageYears} yo`;
    } else {
      return `${ageMonths} mo`;
    }
  };

  const getPetStats = (pet: Pet) => {
    return petStats[pet.id] || { unreadMessages: 0, alerts: 0, allGood: true };
  };

  return (
    <Dialog
      open={open}
      onClose={showCloseButton ? onClose : undefined}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: theme.shadows[24],
          // Apply safe area insets for full-screen dialogs on mobile
          ...(isMobile ? {
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          } : {}),
        }
      }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4, md: 5 }, position: 'relative' }}>
        {/* Close Button (optional) */}
        {showCloseButton && onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                bgcolor: theme.palette.primary.main,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              <PetsIcon sx={{ fontSize: 40 }} />
            </Avatar>
          </motion.div>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Welcome back, {userName}! 👋
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            Who are you caring for today?
          </Typography>
        </Box>

        {/* Pet Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <AnimatePresence>
            {pets.map((pet, index) => {
              const stats = getPetStats(pet);
              const isHovered = hoveredPetId === pet.id;

              return (
                <Grid item xs={12} sm={6} md={pets.length > 2 ? 4 : 6} key={pet.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card
                      onMouseEnter={() => setHoveredPetId(pet.id)}
                      onMouseLeave={() => setHoveredPetId(null)}
                      sx={{
                        height: '100%',
                        position: 'relative',
                        overflow: 'visible',
                        background: theme.palette.mode === 'light'
                          ? 'rgba(255, 255, 255, 0.9)'
                          : 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: `2px solid ${
                          isHovered
                            ? theme.palette.primary.main
                            : alpha(theme.palette.divider, 0.1)
                        }`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        boxShadow: isHovered
                          ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`
                          : theme.shadows[2],
                      }}
                    >
                      <CardActionArea
                        onClick={() => handlePetSelect(pet)}
                        sx={{ height: '100%', p: 3 }}
                      >
                        <CardContent sx={{ p: 0 }}>
                          <Stack alignItems="center" spacing={2}>
                            {/* Pet Avatar */}
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={
                                stats.allGood ? (
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: 'success.main',
                                      border: `2px solid ${theme.palette.background.paper}`,
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                                  </Avatar>
                                ) : stats.alerts > 0 ? (
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: 'warning.main',
                                      border: `2px solid ${theme.palette.background.paper}`,
                                    }}
                                  >
                                    <WarningIcon sx={{ fontSize: 16 }} />
                                  </Avatar>
                                ) : null
                              }
                            >
                              <LazyAvatar
                                src={pet.imageUrl}
                                alt={pet.name}
                                sx={{
                                  width: 100,
                                  height: 100,
                                  border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  boxShadow: theme.shadows[4],
                                }}
                              >
                                <PetsIcon sx={{ fontSize: 50 }} />
                              </LazyAvatar>
                            </Badge>

                            {/* Pet Name */}
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                textAlign: 'center',
                              }}
                            >
                              {pet.name}
                            </Typography>

                            {/* Pet Info */}
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                icon={<CakeIcon />}
                                label={getPetAge(pet)}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={pet.gender || 'Unknown'}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>

                            {/* Breed */}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textAlign: 'center' }}
                            >
                              {pet.breed || pet.species || 'Pet'}
                            </Typography>

                            {/* Stats/Badges */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                              {stats.unreadMessages > 0 && (
                                <Chip
                                  icon={<MailIcon />}
                                  label={`${stats.unreadMessages} unread`}
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              {stats.alerts > 0 && (
                                <Chip
                                  icon={<WarningIcon />}
                                  label={`${stats.alerts} alert${stats.alerts > 1 ? 's' : ''}`}
                                  size="small"
                                  color="warning"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                              {stats.allGood && stats.unreadMessages === 0 && stats.alerts === 0 && (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="All good"
                                  size="small"
                                  color="success"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </CardActionArea>

                      {/* Hover Effect Glow */}
                      {isHovered && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: 3,
                            background: `radial-gradient(circle at 50% 50%, ${alpha(
                              theme.palette.primary.main,
                              0.1
                            )} 0%, transparent 70%)`,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </AnimatePresence>
        </Grid>

        {/* Footer - Manage All Pets Link */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            onClick={handleManageAllPets}
            endIcon={<HeartIcon />}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            Manage All Pets
          </Button>
        </Box>

        {/* Helper Text */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'text.secondary',
            mt: 3,
          }}
        >
          You can switch pets anytime from the top menu
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default PetSelectorModal;

